import React, { useState } from "react";
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Globe,
  User,
  Award,
  Calendar,
} from "lucide-react";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const EligibilityCheck = () => {
  const { t } = useTranslation();
  const ec = (key: string) => t(`eligibilityCheck.${key}`);
  const fb = (key: string) => t(`eligibilityCheck.feedback.${key}`);

  const [purpose, setPurpose] = useState("");
  const [germanSkill, setGermanSkill] = useState("");
  const [lastEducation, setLastEducation] = useState("");
  const [result, setResult] = useState("");
  const [passingYear, setPassingYear] = useState("");
  const [age, setAge] = useState("");
  const [feedback, setFeedback] = useState<React.ReactNode>("");
  const [isEligible, setIsEligible] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const viewCourseBtn = (to: string) => (
    <Link
      to={to}
      className="inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-[#009fe3] hover:bg-[#007cb5] text-white px-6 py-3 rounded-lg font-semibold text-base transition-all duration-200 shadow-md hover:shadow-lg"
    >
      {fb("viewCourse")}
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  );

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));

    const numericResult = parseFloat(result);
    const userAge = parseInt(age);

    if (!purpose || !germanSkill || !lastEducation || !result || !passingYear || !age) {
      setFeedback(fb("completeAll"));
      setIsEligible(false);
      setIsSubmitting(false);
      return;
    }

    if (
      (lastEducation === "SSC" || lastEducation === "HSC" || lastEducation === "Diploma") &&
      numericResult < 3.0
    ) {
      setFeedback(
        <div className="space-y-4">
          <p className="text-xl font-bold text-red-600">{fb("belowGpa.title")}</p>
          <p className="text-gray-600 leading-relaxed text-base">{fb("belowGpa.body")}</p>
        </div>,
      );
      setIsEligible(false);
      setIsSubmitting(false);
      return;
    }

    const ageWarning =
      userAge >= 30 ? (
        <span className="block text-sm font-normal mt-1">{ec("ageWarning")}</span>
      ) : null;

    // Ausbildung Logic
    if (purpose === "Ausbildung") {
      if (lastEducation === "SSC" || lastEducation === "HSC" || lastEducation === "Diploma") {
        if (["b1", "b2", "c1"].includes(germanSkill.toLowerCase())) {
          setFeedback(
            <div className="space-y-4">
              <p className="text-xl font-bold text-green-600">{fb("ausb_b1_low.title")}{ageWarning}</p>
              <p className="text-gray-600 leading-relaxed text-base">{fb("ausb_b1_low.body")}</p>
              {viewCourseBtn("/course-details/ausbildung-preparatory-course-without-language")}
            </div>,
          );
          setIsEligible(true);
        } else if (["a1", "a2"].includes(germanSkill.toLowerCase()) || germanSkill.toLowerCase() === "none") {
          setFeedback(
            <div className="space-y-4">
              <p className="text-xl font-bold text-yellow-600">{fb("ausb_a1_low.title")}</p>
              <p className="text-gray-600 leading-relaxed text-base">
                {fb("ausb_a1_low.body")}
                {ageWarning && <span className="block text-sm text-gray-600 mt-2">{ec("ageWarning")}</span>}
              </p>
              <p className="text-gray-600 leading-relaxed text-base">{fb("ausb_a1_low.body2")}</p>
              {viewCourseBtn("/course-details/ausbildung-preparatory-course")}
            </div>,
          );
          setIsEligible(true);
        }
      } else if (lastEducation === "Bachelor" || lastEducation === "Masters") {
        if (["b1", "b2", "c1"].includes(germanSkill.toLowerCase())) {
          setFeedback(
            <div className="space-y-4">
              <p className="text-xl font-bold text-green-600">{fb("ausb_b1_high.title")}{ageWarning}</p>
              <p className="text-gray-600 leading-relaxed text-base">{fb("ausb_b1_high.body")}</p>
              <p className="text-gray-600 leading-relaxed text-base">{fb("ausb_b1_high.body2")}</p>
              {viewCourseBtn("/course-details/student-visa-processing-course")}
            </div>,
          );
          setIsEligible(true);
        } else if (["a1", "a2"].includes(germanSkill.toLowerCase()) || germanSkill.toLowerCase() === "none") {
          setFeedback(
            <div className="space-y-4">
              <p className="text-xl font-bold text-yellow-600">{fb("ausb_a1_high.title")}</p>
              <p className="text-gray-600 leading-relaxed text-base">
                {fb("ausb_a1_high.body")}
                {ageWarning && <span className="block text-sm text-gray-600 mt-2">{ec("ageWarning")}</span>}
              </p>
              <p className="text-gray-600 leading-relaxed text-base">{fb("ausb_a1_high.body2")}</p>
              {viewCourseBtn("/course-details/student-visa-processing-course")}
            </div>,
          );
          setIsEligible(true);
        }
      }
    }

    // Ausbildung Searching Visa Logic
    else if (purpose === "Ausbildung Searching Visa") {
      if (lastEducation === "SSC" || lastEducation === "HSC" || lastEducation === "Diploma") {
        if (["b1", "b2", "c1"].includes(germanSkill.toLowerCase())) {
          setFeedback(
            <div className="space-y-4">
              <p className="text-xl font-bold text-green-600">{fb("searchvisa_b1_low.title")}{ageWarning}</p>
              <p className="text-gray-600 leading-relaxed text-base">{fb("searchvisa_b1_low.body")}</p>
              <p className="text-gray-600 leading-relaxed text-base">{fb("searchvisa_b1_low.body2")}</p>
              {viewCourseBtn("/course-details/ausbildung-searching-visa-processing-course")}
            </div>,
          );
          setIsEligible(true);
        } else {
          setFeedback(
            <div className="space-y-4">
              <p className="text-xl font-bold text-red-600">{fb("searchvisa_noLang_low.title")}</p>
              <p className="text-gray-600 leading-relaxed text-base">
                {fb("searchvisa_noLang_low.body")}
                {ageWarning && <span className="block text-sm text-gray-600 mt-2">{ec("ageWarning")}</span>}
              </p>
              <Link
                to="/course-details/german-language-course"
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-[#009fe3] hover:bg-[#007cb5] text-white px-6 py-3 rounded-lg font-semibold text-base transition-all duration-200 shadow-md hover:shadow-lg"
              >
                {fb("germanCourses")}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>,
          );
          setIsEligible(false);
        }
      } else if (lastEducation === "Bachelor" || lastEducation === "Masters") {
        if (["b1", "b2", "c1"].includes(germanSkill.toLowerCase())) {
          setFeedback(
            <div className="space-y-4">
              <p className="text-xl font-bold text-green-600">{fb("searchvisa_b1_high.title")}{ageWarning}</p>
              <p className="text-gray-600 leading-relaxed text-base">{fb("searchvisa_b1_high.body")}</p>
              <p className="text-gray-600 leading-relaxed text-base">{fb("searchvisa_b1_high.body2")}</p>
              {viewCourseBtn("/course-details/student-visa-processing-course")}
            </div>,
          );
          setIsEligible(true);
        } else {
          setFeedback(
            <div className="space-y-4">
              <p className="text-xl font-bold text-red-600">{fb("searchvisa_noLang_high.title")}</p>
              <p className="text-gray-600 leading-relaxed text-base">
                {fb("searchvisa_noLang_high.body")}
                {ageWarning && <span className="block text-sm text-gray-600 mt-2">{ec("ageWarning")}</span>}
              </p>
              <Link
                to="/course/courses"
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-[#009fe3] hover:bg-[#007cb5] text-white px-6 py-3 rounded-lg font-semibold text-base transition-all duration-200 shadow-md hover:shadow-lg"
              >
                {fb("exploreCourses")}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>,
          );
          setIsEligible(false);
        }
      }
    }

    // Masters Logic
    else if (purpose === "Masters") {
      if (lastEducation === "Diploma") {
        setFeedback(
          <div className="space-y-4">
            <p className="text-xl font-bold text-red-600">{fb("masters_diploma.title")}</p>
            <p className="text-gray-600 leading-relaxed text-base">{fb("masters_diploma.body")}</p>
            <p className="text-gray-600 leading-relaxed text-base">{fb("masters_diploma.body2")}</p>
            {viewCourseBtn("/course-details/ausbildung-preparatory-course")}
          </div>,
        );
        setIsEligible(false);
      } else if (lastEducation === "Bachelor" || lastEducation === "Masters") {
        setFeedback(
          <div className="space-y-4">
            <p className="text-xl font-bold text-green-600">{fb("masters_eligible.title")}</p>
            <p className="text-gray-600 leading-relaxed text-base">{fb("masters_eligible.body")}</p>
            <p className="text-gray-600 leading-relaxed text-base">{fb("masters_eligible.body2")}</p>
            {viewCourseBtn("/course-details/student-visa-processing-course")}
          </div>,
        );
        setIsEligible(true);
      } else {
        setFeedback(
          <div className="space-y-4">
            <p className="text-xl font-bold text-red-600">{fb("masters_needBachelor.title")}</p>
            <p className="text-gray-600 leading-relaxed text-base">{fb("masters_needBachelor.body")}</p>
          </div>,
        );
        setIsEligible(false);
      }
    }

    // Bachelor Logic
    else if (purpose === "Bachelor") {
      if (lastEducation === "Diploma") {
        setFeedback(
          <div className="space-y-4">
            <p className="text-xl font-bold text-red-600">{fb("bach_diploma.title")}</p>
            <p className="text-gray-600 leading-relaxed text-base">{fb("bach_diploma.body")}</p>
            <p className="text-gray-600 leading-relaxed text-base">{fb("bach_diploma.body2")}</p>
            {viewCourseBtn("/course-details/ausbildung-preparatory-course")}
          </div>,
        );
        setIsEligible(false);
      } else if (lastEducation === "HSC") {
        if (numericResult >= 3.0) {
          setFeedback(
            <div className="space-y-4">
              <p className="text-xl font-bold text-red-600">{fb("bach_hsc_study.title")}</p>
              <p className="text-gray-600 leading-relaxed text-base">{fb("bach_hsc_study.body")}</p>
            </div>,
          );
          setIsEligible(false);
        } else {
          setFeedback(
            <div className="space-y-4">
              <p className="text-xl font-bold text-red-600">{fb("belowGpa.title")}</p>
              <p className="text-gray-600 leading-relaxed text-base">{fb("belowGpa.body")}</p>
            </div>,
          );
          setIsEligible(false);
        }
      } else if (lastEducation === "SSC") {
        setFeedback(
          <div className="space-y-4">
            <p className="text-xl font-bold text-red-600">{fb("bach_ssc.title")}</p>
            <p className="text-gray-600 leading-relaxed text-base">{fb("bach_ssc.body")}</p>
          </div>,
        );
        setIsEligible(false);
      } else {
        setFeedback(
          <div className="space-y-4">
            <p className="text-xl font-bold text-yellow-600">{fb("bach_considerMasters.title")}</p>
            <p className="text-gray-600 leading-relaxed text-base">{fb("bach_considerMasters.body")}</p>
            <p className="text-gray-600 leading-relaxed text-base">{fb("bach_considerMasters.body2")}</p>
            {viewCourseBtn("/course-details/student-visa-processing-course")}
          </div>,
        );
        setIsEligible(true);
      }
    }

    try {
      await fetch(`${API_BASE}/api/eligibility-check/increment`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
    } catch (error) {
      console.error("Failed to increment counter:", error);
    }

    setIsSubmitting(false);
  };

  const getCompletionPercentage = () => {
    const fields = [purpose, germanSkill, lastEducation, result, passingYear, age];
    const completed = fields.filter((field) => field.trim() !== "").length;
    return (completed / fields.length) * 100;
  };

  const purposeOptions = [
    { value: "Ausbildung", icon: <Award className="w-5 h-5" />, desc: ec("purposeDescs.ausbildung") },
    { value: "Ausbildung Searching Visa", icon: <Globe className="w-5 h-5" />, desc: ec("purposeDescs.ausbildungSearchVisa") },
    { value: "Bachelor", icon: <FileText className="w-5 h-5" />, desc: ec("purposeDescs.bachelor") },
    { value: "Masters", icon: <User className="w-5 h-5" />, desc: ec("purposeDescs.masters") },
  ];

  const germanLevels = ["None", "A1", "A2", "B1", "B2", "C1"];
  const educationLevels = ["SSC", "HSC", "Diploma", "Bachelor", "Masters"];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#009fe3]/3 via-white to-blue-50/30">
      <Breadcrumb title={ec("breadcrumb")} />

      <div className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              {ec("pageTitle")}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {ec("pageSubtitle")}
            </p>

            {/* Progress Bar */}
            <div className="max-w-md mx-auto mt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">{ec("progress")}</span>
                <span className="text-sm font-medium text-[#009fe3]">
                  {Math.round(getCompletionPercentage())}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-[#009fe3] to-[#007cb5] h-2 rounded-full transition-all duration-500"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Form Section */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-r from-[#009fe3] to-[#007cb5] rounded-xl flex items-center justify-center">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{ec("formTitle")}</h3>
                    <p className="text-gray-600 text-sm">{ec("formSubtitle")}</p>
                  </div>
                </div>

                {/* Purpose Selection */}
                <div className="mb-6">
                  <label className="flex items-center gap-2 font-semibold mb-4 text-gray-800">
                    <Globe className="w-4 h-4 text-[#009fe3]" />
                    {ec("purposeLabel")}
                  </label>
                  <div className="grid md:grid-cols-2 gap-3">
                    {purposeOptions.map((option) => (
                      <label
                        key={option.value}
                        className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 hover:shadow-md group ${
                          purpose === option.value
                            ? "border-[#009fe3] bg-[#009fe3]/5 shadow-md"
                            : "border-gray-200 hover:border-[#009fe3]/30"
                        }`}
                      >
                        <input
                          type="radio"
                          name="purpose"
                          value={option.value}
                          checked={purpose === option.value}
                          onChange={(e) => setPurpose(e.target.value)}
                          className="sr-only"
                        />
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                              purpose === option.value
                                ? "bg-[#009fe3] text-white"
                                : "bg-gray-100 text-gray-600 group-hover:bg-[#009fe3]/10 group-hover:text-[#009fe3]"
                            }`}
                          >
                            {option.icon}
                          </div>
                          <div className="flex-1">
                            <div className={`font-semibold text-sm ${purpose === option.value ? "text-[#009fe3]" : "text-gray-900"}`}>
                              {option.value}
                            </div>
                            <div className="text-xs text-gray-600">{option.desc}</div>
                          </div>
                        </div>
                        {purpose === option.value && (
                          <div className="absolute top-2 right-2">
                            <CheckCircle className="w-5 h-5 text-[#009fe3]" />
                          </div>
                        )}
                      </label>
                    ))}
                  </div>
                </div>

                {/* German Language Skill */}
                <div className="mb-6">
                  <label className="flex items-center gap-2 font-semibold mb-4 text-gray-800">
                    <Globe className="w-4 h-4 text-[#009fe3]" />
                    {ec("germanLevelLabel")}
                  </label>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                    {germanLevels.map((level) => (
                      <label
                        key={level}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md text-center ${
                          germanSkill === level
                            ? "border-[#009fe3] bg-[#009fe3]/5 shadow-md"
                            : "border-gray-200 hover:border-[#009fe3]/30"
                        }`}
                      >
                        <input
                          type="radio"
                          name="germanSkill"
                          value={level}
                          checked={germanSkill === level}
                          onChange={(e) => setGermanSkill(e.target.value)}
                          className="sr-only"
                        />
                        <div className={`font-semibold text-sm ${germanSkill === level ? "text-[#009fe3]" : "text-gray-700"}`}>
                          {level}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Education Level */}
                <div className="mb-6">
                  <label className="flex items-center gap-2 font-semibold mb-4 text-gray-800">
                    <Award className="w-4 h-4 text-[#009fe3]" />
                    {ec("educationLabel")}
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {educationLevels.map((edu) => (
                      <label
                        key={edu}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md text-center ${
                          lastEducation === edu
                            ? "border-[#009fe3] bg-[#009fe3]/5 shadow-md"
                            : "border-gray-200 hover:border-[#009fe3]/30"
                        }`}
                      >
                        <input
                          type="radio"
                          name="lastEducation"
                          value={edu}
                          checked={lastEducation === edu}
                          onChange={(e) => setLastEducation(e.target.value)}
                          className="sr-only"
                        />
                        <div className={`font-semibold text-sm ${lastEducation === edu ? "text-[#009fe3]" : "text-gray-700"}`}>
                          {edu}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Result, Passing Year & Age */}
                {lastEducation && (
                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    <div>
                      <label className="flex items-center gap-2 mb-3 text-gray-800 font-semibold">
                        <Award className="w-4 h-4 text-[#009fe3]" />
                        {ec("resultLabel")}
                      </label>
                      <input
                        type="text"
                        value={result}
                        onChange={(e) => setResult(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:outline-none focus:border-[#009fe3] focus:ring-4 focus:ring-[#009fe3]/10 transition-all duration-200"
                        placeholder={ec("resultPlaceholder")}
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 mb-3 text-gray-800 font-semibold">
                        <Calendar className="w-4 h-4 text-[#009fe3]" />
                        {ec("passingYearLabel")}
                      </label>
                      <input
                        type="text"
                        value={passingYear}
                        onChange={(e) => setPassingYear(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:outline-none focus:border-[#009fe3] focus:ring-4 focus:ring-[#009fe3]/10 transition-all duration-200"
                        placeholder={ec("passingYearPlaceholder")}
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 mb-3 text-gray-800 font-semibold">
                        <User className="w-4 h-4 text-[#009fe3]" />
                        {ec("ageLabel")}
                      </label>
                      <input
                        type="text"
                        value={age}
                        onChange={(e) => setAge(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:outline-none focus:border-[#009fe3] focus:ring-4 focus:ring-[#009fe3]/10 transition-all duration-200"
                        placeholder={ec("agePlaceholder")}
                      />
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || getCompletionPercentage() < 100}
                  className="w-full bg-gradient-to-r from-[#009fe3] to-[#007cb5] hover:from-[#007cb5] hover:to-[#006ba3] disabled:from-gray-400 disabled:to-gray-500 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-200 transform hover:scale-[1.02] disabled:transform-none shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>{ec("analyzing")}</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      <span>{ec("checkBtn")}</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Results Section */}
            <div className="lg:col-span-1">
              <div
                className={`sticky top-8 p-8 rounded-3xl shadow-xl border transition-all duration-500 ${
                  isEligible === null
                    ? "bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200"
                    : isEligible
                      ? "bg-gradient-to-br from-green-50 to-emerald-100 border-green-200"
                      : "bg-gradient-to-br from-red-50 to-rose-100 border-red-200"
                }`}
              >
                {isEligible !== null ? (
                  <div className="text-center animate-in fade-in duration-500">
                    <div className={`mb-6 p-4 rounded-full w-fit mx-auto ${isEligible ? "bg-green-100" : "bg-red-100"}`}>
                      {isEligible ? (
                        <CheckCircle className="w-16 h-16 text-green-600" />
                      ) : (
                        <XCircle className="w-16 h-16 text-red-600" />
                      )}
                    </div>

                    <h3 className={`text-2xl font-bold mb-6 ${isEligible ? "text-green-700" : "text-red-700"}`}>
                      {isEligible ? ec("congratulations") : ec("assessmentResults")}
                    </h3>

                    <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                      <div className="text-gray-800 text-base leading-relaxed text-left">
                        {feedback}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-[#009fe3] to-[#007cb5] rounded-full flex items-center justify-center">
                      <AlertCircle className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-700 mb-4">{ec("readyTitle")}</h3>
                    <p className="text-gray-600 leading-relaxed mb-6">{ec("readyDesc")}</p>
                    <div className="text-sm text-gray-500">
                      <span className="font-medium">
                        {Math.round(getCompletionPercentage())}% {ec("complete")}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EligibilityCheck;
